import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import produce from 'immer';
import { omit, reverse } from 'lodash';

import useToastMsg from '../../common/useToastMsg';
import ProductApi from '../../../lib/api/product/ProductApi';
import { setRequestForm } from '../../../lib/common';
import { setChoiceOption, setProductVersionInfo } from '../../../lib/utils';
import { PRODUCT_OPTION_ITEM_MODEL, PRODUCT_STOCK_AND_OPTION_MODEL } from '../../../store/modules/product/models';
import { PRODUCT } from '../../../constants/queryKeys';
import { COMMON_RES_CODE } from '../../../constants/responseCode/commonResCode';
import {
  FULFILLMENT_PRODUCT_MANAGEMENT_TYPE_LIST,
  PRODUCT_MANAGEMENT_TYPE,
} from '../../../constants/service/app/productConstants';
import productToastMsg from '../../../constants/service/toastMsg/productToastMsg';
import { extractErrorMessage } from '../../../lib/utils/checkErrorResponse';

const setVersionDetail = ({ key, initialData, result }) => {
  const res = produce(initialData, (draft) => {
    const { brandFeeInfoList } = result.data[key].proStockAndOption;
    const isFulfillmentProduct = brandFeeInfoList.find((item) =>
      FULFILLMENT_PRODUCT_MANAGEMENT_TYPE_LIST.includes(item.productManagementType),
    );
    /**
     *  default 수수료 정보
     */
    const targetFeeInfo = brandFeeInfoList.find((item) => PRODUCT_MANAGEMENT_TYPE.ENTER === item.productManagementType);

    /**
     *  타겟 버전
     */
    draft[key] = {
      ...result.data[key],
      proPayMethod: {
        ...draft[key].proPayMethod,
        ...result.data[key].proPayMethod,
      },
      proStockAndOption: {
        ...draft[key].proStockAndOption,
        ...result.data[key].proStockAndOption,
        optionList: result.data[key].proStockAndOption.optionList.length
          ? result.data[key].proStockAndOption.optionList.map((item) => ({
              ...PRODUCT_OPTION_ITEM_MODEL,
              ...item,
              productManagementTypeSelectList: isFulfillmentProduct
                ? PRODUCT_OPTION_ITEM_MODEL.productManagementTypeSelectList.map((subItem) => ({
                    ...subItem,
                    disabled: false,
                  }))
                : PRODUCT_OPTION_ITEM_MODEL.productManagementTypeSelectList,
            }))
          : [],
        feeInfo: {
          ...draft[key].proStockAndOption.feeInfo,
          ...targetFeeInfo,
          ...result.data[key].proStockAndOption.feeInfo,
          productManagementTypeSelectList: isFulfillmentProduct
            ? draft[key].proStockAndOption.feeInfo.productManagementTypeSelectList.map((subItem) => ({
                ...subItem,
                disabled: false,
              }))
            : draft[key].proStockAndOption.feeInfo.productManagementTypeSelectList,
        },
      },
    };

    /**
     * 카테고리
     */
    draft[key].proCategory.categoryList = draft[key].proCategory.categoryList.map((item) => {
      const subCategory = item.subCategoryId.map((id) => {
        const sub = draft.subProductCategorySelectList[item.mainCategoryId].find((category) => category.id === id);
        return {
          id: sub.id,
          name: sub.name,
          productCategoryUpdateLogId: null,
        };
      });
      return {
        id: item.mainCategoryId,
        name: item.mainCategoryName,
        productCategoryUpdateLogId: null,
        subCategory,
      };
    });

    /**
     *  타겟 버전에 옵션 리스트가 있다면 choiceOption 에 값 설정해주기
     */
    if (result.data[key].proStockAndOption.isOption) {
      draft[key].proStockAndOption.choiceOption = setChoiceOption(result.data[key].proStockAndOption);

      /**
       *  옵션 리스트에서 id 값 빼주기
       */
      draft[key].proStockAndOption.optionList = draft[key].proStockAndOption.optionList.map((item) =>
        omit(item, ['id', 'productOptionKeyId']),
      );
    } else {
      draft[key].proStockAndOption.choiceOption = PRODUCT_STOCK_AND_OPTION_MODEL.choiceOption;
    }
  });
  return res[key];
};

const queryFn = async (productId, initialData) => {
  const result = await ProductApi.getProductVersion(setRequestForm({ productId }, true));

  if (result?.status !== COMMON_RES_CODE.SUCCESS) {
    throw result.msg;
  }

  return produce(initialData, (draft) => {
    /**
     *  현재 버전 정보
     */
    draft.currentVersionInfo = setProductVersionInfo(result.data.currentVersionInfo);

    /**
     *  상품 버전 리스트
     */
    draft.productVersionList = result.data.productVersionList.map((item, index) => ({
      id: item.id,
      name: setProductVersionInfo(item, index + 1),
    }));

    /**
     *  상품 버전 리스트를 내림차순으로 정렬
     */
    draft.productVersionList = reverse(draft.productVersionList);

    /**
     *  상풍 버전 리스트에서 첫 번째 버전을 기본으로 보여줍니다.
     */
    draft.targetVersionDetailId = draft.productVersionList.length ? draft.productVersionList[0].id : null;

    /**
     * 상품 속성
     */
    draft.productPropertyList = result.data.productPropertyList;

    draft.subProductCategorySelectList = result.data.subProductCategorySelectList;

    /**
     * 현재 버전
     */
    draft.currentVersionDetail = setVersionDetail({
      key: 'currentVersionDetail',
      initialData: draft,
      result,
    });

    /**
     *  비교할 대상 버전
     */
    if (result.data.targetVersionDetail) {
      draft.targetVersionDetail = setVersionDetail({
        key: 'targetVersionDetail',
        initialData: draft,
        result,
      });
      /**
       *  비교할 대상 버전의 거절 사유
       */
      if (result.data.rejectReason && result.data.rejectReason.reason) {
        draft.rejectReason = result.data.rejectReason;
      }
    } else {
      draft.targetVersionDetail = initialData.targetVersionDetail;
    }
  });
};

const useProductVersion = (productId, options) => {
  const query = useQuery([...PRODUCT.PRODUCT_VERSION], () => queryFn(productId, options.initialData), options);
  const queryClient = useQueryClient();
  const toastMsg = useToastMsg();

  const getProductVersionDetailMutate = useMutation({
    mutationFn: async (payload) => {
      const result = await ProductApi.getProductVersionDetail(setRequestForm(payload));

      if (result?.status !== COMMON_RES_CODE.SUCCESS) {
        throw new Error(result.msg);
      }

      queryClient.setQueryData([...PRODUCT.PRODUCT_VERSION], (prev) => {
        return produce(prev, (draft) => {
          /**
           *  비교할 대상 버전
           */
          if (result.data.targetVersionDetail) {
            draft.targetVersionDetail = setVersionDetail({
              key: 'targetVersionDetail',
              initialData: prev,
              result,
            });

            /**
             *  비교할 대상 버전의 id
             */
            draft.targetVersionDetailId = result.data.targetVersionDetailId;

            /**
             *  비교할 대상 버전의 거절 사유
             */
            if (result.data.rejectReason && result.data.rejectReason.reason)
              draft.rejectReason = result.data.rejectReason;
          }
        });
      });
    },
    onSuccess: () => {
      toastMsg.success(productToastMsg.GET_PRODUCT_VERSION_DETAIL_REQUEST_SUCCESS);
    },
    onError: (e) => {
      toastMsg.error(extractErrorMessage(e, productToastMsg.GET_PRODUCT_VERSION_DETAIL_REQUEST_ERROR));
    },
  });
  return { ...query, getProductVersionDetailMutate };
};

export default useProductVersion;
